import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { Button } from "@/components/ui/Button";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { useHubToast } from "@/components/ui/HubToast";
import { MegaventoryProductExternalLink } from "@/components/mrp/MegaventoryPurchaseOrderLinks";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { Input, Label, TextArea } from "@/components/ui/Input";
import {
  MRP_LOCATIONS,
  MRP_SKU_PLANNING_SELECT_OPTIONS,
  type MrpLocation,
  type MrpSkuDetail,
  type MrpSkuPlanningMode,
} from "@/lib/mrp";
import {
  amlStandardPackSizesAreDirty,
  buildAmlStandardPackSizePatches,
  SkuStandardPackSizesSection,
  useAmlPackSizeDrafts,
} from "@/pages/mrp/SkuStandardPackSizesSection";
import { buildSetBomRulesDeepLink } from "@/lib/bomDeepLink";
import {
  formatSkuPropertiesDirtyHint,
  skuPropertiesDirtyFieldLabels,
} from "@/pages/mrp/skuPropertiesDirtyFieldLabels";
import type { SkuFormValues } from "@/pages/mrp/skuPropertiesFormTypes";

function triToForm(value: boolean | null): string {
  if (value === null) return "";
  return value ? "true" : "false";
}

function detailToForm(detail: MrpSkuDetail): SkuFormValues {
  const reserves = detail.mrp_reserve_by_location ?? {};
  return {
    sku: detail.sku,
    planning_mode: detail.planning_mode ?? "",
    mrp_reserve_by_location: Object.fromEntries(
      MRP_LOCATIONS.map(({ code }) => [
        code,
        reserves[code] != null ? String(reserves[code]) : "",
      ]),
    ) as Record<MrpLocation, string>,
    mrp_replenish_to_by_location: Object.fromEntries(
      MRP_LOCATIONS.map(({ code }) => {
        const replen = detail.mrp_replenish_to_by_location?.[code];
        return [code, replen != null ? String(replen) : ""];
      }),
    ) as Record<MrpLocation, string>,
    serialize: detail.serialize,
    template_id: detail.template_id == null ? "" : String(detail.template_id),
    manufacture: detail.manufacture,
    for_sale: triToForm(detail.for_sale),
    advertised: detail.advertised,
    service: detail.service,
    build_to_order: detail.build_to_order,
    for_sale_deployed: detail.for_sale_deployed,
    obsolete: detail.obsolete,
    under_engineering_review: detail.under_engineering_review,
    cots: triToForm(detail.cots ?? null),
    erp_skip_import: detail.erp_skip_import,
    mv_description: detail.mv_description ?? "",
    mv_variant: detail.mv_variant ?? "",
    mv_category_id: detail.mv_category_id == null ? "" : String(detail.mv_category_id),
    mv_product_type: detail.mv_product_type ?? "",
    mv_sellable: triToForm(detail.mv_sellable),
    mv_purchasable: triToForm(detail.mv_purchasable),
    mv_unit: detail.mv_unit ?? "",
    mv_image_url: detail.mv_image_url ?? "",
    mv_supply_chain_notes: detail.mv_supply_chain_notes ?? "",
    hub_supply_chain_notes: detail.hub_supply_chain_notes ?? "",
    fair_market_value: detail.fair_market_value == null || detail.fair_market_value === "" ? "" : String(detail.fair_market_value),
    mv_hts_code: detail.mv_hts_code ?? "",
  };
}

function parseTriBool(raw: string): boolean | null | undefined {
  if (raw === "") return null;
  if (raw === "true") return true;
  if (raw === "false") return false;
  return undefined;
}

function triEqual(a: boolean | null, b: boolean | null): boolean {
  return a === b;
}

function parseOptionalInt(raw: string): number | null | undefined {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number.parseInt(t, 10);
  return Number.isInteger(n) ? n : undefined;
}

function parseOptionalMoney(raw: string): number | null | undefined {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
}

function buildSkuPatch(detail: MrpSkuDetail, values: SkuFormValues): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  const linkedMv = detail.mv_id != null;
  const catalogDescriptionControlled = detail.template_controls_catalog_description === true;

  if (!linkedMv && values.sku.trim() !== detail.sku) {
    patch.sku = values.sku.trim();
  }

  const prevPlanning = detail.planning_mode ?? null;
  const nextPlanning =
    values.planning_mode === "" ? null : (values.planning_mode as MrpSkuPlanningMode);
  if (nextPlanning !== prevPlanning) {
    patch.planning_mode = nextPlanning;
  }

  const detailReserveByLocation = detail.mrp_reserve_by_location ?? {};

  const reservePatch: Record<string, number | null> = {};
  let reserveChanged = false;
  for (const { code } of MRP_LOCATIONS) {
    const raw = values.mrp_reserve_by_location[code] ?? "";
    const parsed = parseOptionalInt(raw);
    if (parsed === undefined) {
      throw new Error(`MRP reserve (${code}) must be an integer or empty.`);
    }
    const nextVal = parsed;
    const prevVal = detailReserveByLocation[code];
    if (nextVal !== (prevVal === undefined ? null : prevVal)) {
      reserveChanged = true;
      reservePatch[code] = nextVal;
    }
  }
  const replenishPatch: Record<string, number | null> = {};
  let replenishChanged = false;
  for (const { code } of MRP_LOCATIONS) {
    const rawReplenish = values.mrp_replenish_to_by_location[code] ?? "";
    const parsedReplenish = parseOptionalInt(rawReplenish);
    if (parsedReplenish === undefined) {
      throw new Error(`Replenish to (${code}) must be an integer or empty.`);
    }
    const nextReplenish = parsedReplenish === null || parsedReplenish === 0 ? null : parsedReplenish;

    const rawReserve = values.mrp_reserve_by_location[code] ?? "";
    const parsedReserve = parseOptionalInt(rawReserve);
    if (parsedReserve === undefined) {
      throw new Error(`MRP reserve (${code}) must be an integer or empty.`);
    }
    const effectiveReserve = parsedReserve;

    const previousReplenish = detail.mrp_replenish_to_by_location?.[code];
    const previousNormalized = previousReplenish != null && previousReplenish > 0 ? previousReplenish : null;
    if (nextReplenish !== previousNormalized) {
      if (nextReplenish != null && nextReplenish > 0) {
        if (effectiveReserve === null) {
          reservePatch[code] = 0;
          reserveChanged = true;
        } else if (nextReplenish < effectiveReserve) {
          replenishPatch[code] = effectiveReserve;
          replenishChanged = true;
          continue;
        }
      }
      replenishChanged = true;
      replenishPatch[code] = nextReplenish;
    }
  }
  for (const { code } of MRP_LOCATIONS) {
    const reserve = parseOptionalInt(values.mrp_reserve_by_location[code] ?? "");
    const reorderTo = parseOptionalInt(values.mrp_replenish_to_by_location[code] ?? "");
    if (reserve != null && reserve > 0 && reorderTo != null && reorderTo > 0 && reserve > reorderTo) {
      replenishPatch[code] = reserve;
      replenishChanged = true;
    }
  }
  if (reserveChanged) {
    patch.mrp_reserve_by_location = reservePatch;
  }
  if (replenishChanged) {
    patch.mrp_replenish_to_by_location = replenishPatch;
  }

  if (values.serialize !== detail.serialize) patch.serialize = values.serialize;

  const tmpl = parseOptionalInt(values.template_id);
  if (tmpl === undefined) {
    throw new Error("Template ID must be an integer or empty.");
  }
  if (tmpl !== detail.template_id) patch.template_id = tmpl;

  if (values.manufacture !== detail.manufacture) patch.manufacture = values.manufacture;

  const forSale = parseTriBool(values.for_sale);
  if (forSale === undefined) {
    throw new Error("For sale: choose unset, yes, or no.");
  }
  if (!triEqual(forSale, detail.for_sale)) patch.for_sale = forSale;

  if (values.advertised !== detail.advertised) patch.advertised = values.advertised;
  if (values.service !== detail.service) patch.service = values.service;
  if (values.build_to_order !== detail.build_to_order) patch.build_to_order = values.build_to_order;
  if (values.for_sale_deployed !== detail.for_sale_deployed) {
    patch.for_sale_deployed = values.for_sale_deployed;
  }
  if (values.obsolete !== detail.obsolete) patch.obsolete = values.obsolete;
  if (values.under_engineering_review !== detail.under_engineering_review) {
    patch.under_engineering_review = values.under_engineering_review;
  }
  if (values.erp_skip_import !== detail.erp_skip_import) patch.erp_skip_import = values.erp_skip_import;

  const cots = parseTriBool(values.cots);
  if (cots === undefined) {
    throw new Error("Commercial off the shelf: choose unset, yes, or no.");
  }
  if (!triEqual(cots, detail.cots ?? null)) patch.cots = cots;

  const hubNotesNext =
    values.hub_supply_chain_notes.trim() === "" ? null : values.hub_supply_chain_notes.trim();
  if (hubNotesNext !== detail.hub_supply_chain_notes) patch.hub_supply_chain_notes = hubNotesNext;

  const fmvNext = parseOptionalMoney(values.fair_market_value);
  if (fmvNext === undefined) {
    throw new Error("Fair market value must be a non-negative number or empty.");
  }
  const fmvStored = fmvNext === null || fmvNext === 0 ? null : fmvNext;
  const fmvPrevRaw =
    detail.fair_market_value == null || String(detail.fair_market_value).trim() === ""
      ? null
      : Number(detail.fair_market_value);
  const fmvPrev = fmvPrevRaw != null && Number.isFinite(fmvPrevRaw) && fmvPrevRaw > 0 ? fmvPrevRaw : null;
  if (fmvStored !== fmvPrev) patch.fair_market_value = fmvStored;

  if (!linkedMv) {
    if (!catalogDescriptionControlled) {
      const descNext = values.mv_description.trim() === "" ? null : values.mv_description.trim();
      if (descNext !== detail.mv_description) patch.mv_description = descNext;

      const variantNext = values.mv_variant.trim() === "" ? null : values.mv_variant.trim();
      if (variantNext !== detail.mv_variant) patch.mv_variant = variantNext;
    }

    const cat = parseOptionalInt(values.mv_category_id);
    if (cat === undefined) {
      throw new Error("Megaventory category must be selected.");
    }
    if (cat !== detail.mv_category_id) {
      if (cat === null) {
        if (detail.mv_category_id != null) {
          throw new Error("Megaventory category cannot be cleared.");
        }
      } else {
        patch.mv_category_id = cat;
      }
    }

    return patch;
  }

  if (!catalogDescriptionControlled) {
    const descNext = values.mv_description.trim() === "" ? null : values.mv_description.trim();
    if (descNext !== detail.mv_description) patch.mv_description = descNext;

    const variantNext = values.mv_variant.trim() === "" ? null : values.mv_variant.trim();
    if (variantNext !== detail.mv_variant) patch.mv_variant = variantNext;
  }

  const cat = parseOptionalInt(values.mv_category_id);
  if (cat === undefined) {
    throw new Error("Megaventory category must be selected.");
  }
  if (cat !== detail.mv_category_id) {
    if (cat === null) {
      throw new Error("Megaventory category cannot be cleared.");
    }
    patch.mv_category_id = cat;
  }

  const typeNext = values.mv_product_type.trim() === "" ? null : values.mv_product_type.trim();
  if (typeNext !== detail.mv_product_type) {
    if (typeNext === null) {
      throw new Error("Megaventory product type cannot be empty.");
    }
    patch.mv_product_type = typeNext;
  }

  const sell = parseTriBool(values.mv_sellable);
  if (sell === undefined) {
    throw new Error("Sellable: choose unset, yes, or no.");
  }
  if (!triEqual(sell, detail.mv_sellable)) {
    if (sell === null) {
      throw new Error("Sellable cannot be unset for a Megaventory-linked row in this form.");
    }
    patch.mv_sellable = sell;
  }

  const purch = parseTriBool(values.mv_purchasable);
  if (purch === undefined) {
    throw new Error("Purchasable: choose unset, yes, or no.");
  }
  if (!triEqual(purch, detail.mv_purchasable)) {
    if (purch === null) {
      throw new Error("Purchasable cannot be unset for a Megaventory-linked row in this form.");
    }
    patch.mv_purchasable = purch;
  }

  const unitNext = values.mv_unit.trim();
  const unitCur = (detail.mv_unit ?? "").trim();
  if (unitNext !== unitCur) {
    if (unitNext === "") {
      throw new Error("Unit of measure cannot be empty for a Megaventory-linked row.");
    }
    patch.mv_unit = unitNext;
  }

  const imageNext = values.mv_image_url.trim() === "" ? null : values.mv_image_url.trim();
  if (imageNext !== detail.mv_image_url) patch.mv_image_url = imageNext;

  const notesNext = values.mv_supply_chain_notes.trim() === "" ? null : values.mv_supply_chain_notes.trim();
  if (notesNext !== detail.mv_supply_chain_notes) patch.mv_supply_chain_notes = notesNext;

  const htsNext = values.mv_hts_code.trim() === "" ? null : values.mv_hts_code.trim();
  if (htsNext !== detail.mv_hts_code) patch.mv_hts_code = htsNext;

  return patch;
}

function formatDt(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

export function SkuPropertiesForm(props: { sku: string }) {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const mrpReadOnlyTitle = "Your role has read-only access to the MRP hub module.";
  const { amlPackDrafts, setAmlPackDraft, amlRowsQuery } = useAmlPackSizeDrafts(props.sku);
  const amlRows = amlRowsQuery.data?.rows ?? [];
  const amlPackDirty = useMemo(
    () => amlStandardPackSizesAreDirty(amlRows, amlPackDrafts),
    [amlRows, amlPackDrafts],
  );
  const detailQuery = useQuery<MrpSkuDetail, ApiError>({
    queryKey: ["mrp", "skus", "detail", props.sku],
    queryFn: () => apiFetch<MrpSkuDetail>("/api/mrp/skus", { query: { sku: props.sku } }),
  });

  const detail = detailQuery.data;
  const catalogDescriptionControlled = detail?.template_controls_catalog_description === true;
  const { register, handleSubmit, reset, watch, setValue, formState } = useForm<SkuFormValues>();
  const { isDirty, dirtyFields } = formState;
  const hasUnsavedChanges = isDirty || amlPackDirty;
  const [newUnitName, setNewUnitName] = useState("");
  const dirtyFieldHint = useMemo(
    () =>
      formatSkuPropertiesDirtyHint(
        skuPropertiesDirtyFieldLabels(
          dirtyFields as Partial<
            Record<keyof SkuFormValues, boolean | Partial<Record<string, boolean>>>
          >,
          amlRows,
          amlPackDrafts,
          props.sku,
        ),
      ),
    [amlPackDrafts, amlRows, dirtyFields, props.sku],
  );
  const productTypesQuery = useQuery<
    { product_types: { value: string; label: string }[] },
    ApiError
  >({
    queryKey: ["mrp", "mv-product-types"],
    queryFn: () =>
      apiFetch<{ product_types: { value: string; label: string }[] }>("/api/mrp/mv-product-types"),
    enabled: detail?.mv_id != null,
    staleTime: 5 * 60_000,
  });

  const watchedProductType = watch("mv_product_type");
  const productTypeOptions = useMemo(() => {
    const rows = productTypesQuery.data?.product_types ?? [];
    const byValue = new Map(rows.map((row) => [row.value, row] as const));
    const current = (watchedProductType ?? "").trim();
    if (current !== "" && !byValue.has(current)) {
      byValue.set(current, { value: current, label: current });
    }
    return [...byValue.values()].sort((rowA, rowB) =>
      rowA.label.localeCompare(rowB.label, undefined, { sensitivity: "base" }),
    );
  }, [productTypesQuery.data?.product_types, watchedProductType]);

  const productCategoriesQuery = useQuery<
    { product_categories: { value: number; label: string }[] },
    ApiError
  >({
    queryKey: ["mrp", "mv-product-categories"],
    queryFn: () =>
      apiFetch<{ product_categories: { value: number; label: string }[] }>(
        "/api/mrp/mv-product-categories",
      ),
    enabled: detail != null,
    staleTime: 5 * 60_000,
  });

  const watchedCategoryId = watch("mv_category_id");
  const categorySelectOptions = useMemo(() => {
    const rows = productCategoriesQuery.data?.product_categories ?? [];
    const byId = new Map<string, { value: string; label: string }>();
    for (const row of rows) {
      const idStr = String(row.value);
      byId.set(idStr, { value: idStr, label: row.label });
    }
    const current = (watchedCategoryId ?? "").trim();
    if (current !== "" && !byId.has(current)) {
      const name = detail?.mv_category_name?.trim();
      byId.set(current, {
        value: current,
        label: name ? `${name} (${current})` : `Unknown category (${current})`,
      });
    }
    return [...byId.values()].sort((rowA, rowB) =>
      rowA.label.localeCompare(rowB.label, undefined, { sensitivity: "base" }),
    );
  }, [productCategoriesQuery.data?.product_categories, watchedCategoryId, detail?.mv_category_name]);

  const measurementUnitsQuery = useQuery<
    { measurement_units: { value: string; label: string }[] },
    ApiError
  >({
    queryKey: ["mrp", "mv-units-of-measure"],
    queryFn: () =>
      apiFetch<{ measurement_units: { value: string; label: string }[] }>(
        "/api/mrp/mv-units-of-measure",
      ),
    enabled: detail?.mv_id != null,
    staleTime: 5 * 60_000,
  });

  const watchedMvUnit = watch("mv_unit");
  const measurementUnitOptions = useMemo(() => {
    const rows = measurementUnitsQuery.data?.measurement_units ?? [];
    const byValue = new Map(rows.map((row) => [row.value, row] as const));
    const current = (watchedMvUnit ?? "").trim();
    if (current !== "" && !byValue.has(current)) {
      byValue.set(current, { value: current, label: current });
    }
    return [...byValue.values()].sort((rowA, rowB) =>
      rowA.label.localeCompare(rowB.label, undefined, { sensitivity: "base" }),
    );
  }, [measurementUnitsQuery.data?.measurement_units, watchedMvUnit]);

  const createUnitMutation = useMutation<
    { name: string; created: boolean },
    ApiError,
    string
  >({
    mutationFn: (name) =>
      apiFetch<{ name: string; created: boolean }>("/api/mrp/mv-units-of-measure", {
        method: "POST",
        body: { name },
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["mrp", "mv-units-of-measure"] });
      setValue("mv_unit", result.name, { shouldDirty: true, shouldValidate: true });
      setNewUnitName("");
      showToast({
        tone: "success",
        message: result.created
          ? `Created unit of measure "${result.name}".`
          : `Unit of measure "${result.name}" already existed.`,
      });
    },
    onError: (error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const bomTemplatesQuery = useQuery<
    { templates: { label: string; value: number }[] },
    ApiError
  >({
    queryKey: ["bom", "templates"],
    queryFn: () => apiFetch<{ templates: { label: string; value: number }[] }>("/api/bom/templates"),
    enabled: detail != null,
    staleTime: 5 * 60_000,
  });

  const watchedTemplateId = watch("template_id");
  const bomTemplateSelectOptions = useMemo(() => {
    const rows = bomTemplatesQuery.data?.templates ?? [];
    const byId = new Map<string, { value: string; label: string }>();
    for (const row of rows) {
      const idStr = String(row.value);
      byId.set(idStr, { value: idStr, label: row.label });
    }
    const current = (watchedTemplateId ?? "").trim();
    if (current !== "" && !byId.has(current)) {
      byId.set(current, {
        value: current,
        label: `Unknown template (${current})`,
      });
    }
    return [...byId.values()].sort((rowA, rowB) =>
      rowA.label.localeCompare(rowB.label, undefined, { sensitivity: "base" }),
    );
  }, [bomTemplatesQuery.data?.templates, watchedTemplateId]);

  useEffect(() => {
    if (detail) {
      reset(detailToForm(detail));
    }
  }, [detail, reset]);

  const saveMutation = useMutation<void, ApiError, Record<string, unknown>>({
    mutationFn: async (patch) => {
      const amlPatches = buildAmlStandardPackSizePatches(amlRows, amlPackDrafts, props.sku);
      if (Object.keys(patch).length > 0) {
        await apiFetch<MrpSkuDetail>("/api/mrp/skus", {
          method: "PATCH",
          query: { sku: props.sku },
          body: patch,
        });
      }
      for (const amlPatch of amlPatches) {
        await apiFetch(`/api/mrp/aml/${amlPatch.id}`, {
          method: "PATCH",
          body: { standard_pack_size: amlPatch.standard_pack_size },
        });
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus", "detail", props.sku] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "aml"] });
    },
    onError: (error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const clearMvBomRevisionMutation = useMutation<void, ApiError>({
    mutationFn: async () => {
      await apiFetch<MrpSkuDetail>("/api/mrp/skus", {
        method: "PATCH",
        query: { sku: props.sku },
        body: { mv_bom_revision: null },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus", "detail", props.sku] });
    },
    onError: (error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const assignNextBarCodeModelMutation = useMutation<MrpSkuDetail, ApiError>({
    mutationFn: () =>
      apiFetch<MrpSkuDetail>("/api/mrp/skus/assign-next-bar-code-model", {
        method: "POST",
        query: { sku: props.sku },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus", "detail", props.sku] });
    },
    onError: (error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const createMegaventoryProductMutation = useMutation<
    { row: MrpSkuDetail; megaventory_action: string; megaventory_product_id: number },
    ApiError
  >({
    mutationFn: () =>
      apiFetch<{ row: MrpSkuDetail; megaventory_action: string; megaventory_product_id: number }>(
        "/api/mrp/skus/create-megaventory-product",
        {
          method: "POST",
          query: { sku: props.sku },
        },
      ),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "safety-stock-alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["mrp", "skus", "detail", props.sku] });
      showToast({
        tone: "success",
        message: `Megaventory product ${response.megaventory_product_id} (${response.megaventory_action}).`,
      });
    },
    onError: (error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const onSubmit = handleSubmit((values) => {
    if (!hubWritesAllowed) return;
    if (!detail) return;
    try {
      const patch = buildSkuPatch(detail, values);
      const amlPatches = buildAmlStandardPackSizePatches(amlRows, amlPackDrafts, props.sku);
      if (Object.keys(patch).length === 0 && amlPatches.length === 0) {
        showToast({ tone: "info", message: "No changes to save." });
        return;
      }
      saveMutation.mutate(patch);
    } catch (err) {
      showToast({
        tone: "error",
        message: err instanceof Error ? err.message : "Invalid form.",
      });
    }
  });

  const triSelect = (name: keyof SkuFormValues, label: string, disabled?: boolean) => (
    <div>
      <Label>{label}</Label>
      <select
        {...register(name)}
        disabled={disabled}
        className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100"
      >
        <option value="">Unset</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    </div>
  );

  return (
    <div className="space-y-6 pb-24">
      <div className="max-w-3xl space-y-6">
          {detailQuery.isLoading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : detailQuery.error ? (
            <p className="text-sm text-rose-700">{detailQuery.error.message}</p>
          ) : detail ? (
            <form id="sku-edit-form" onSubmit={onSubmit} className="space-y-6">
              <fieldset disabled={!hubWritesAllowed} className="min-w-0 border-0 p-0 m-0 space-y-6 disabled:opacity-90">
              <dl className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                <div>
                  <dt className="font-medium text-slate-500">Internal ID</dt>
                  <dd className="font-mono text-slate-900">{detail.id}</dd>
                </div>
                <div>
                  <dt className="font-medium text-slate-500">Megaventory product ID</dt>
                  <dd className="inline-flex items-center gap-1.5 font-mono text-slate-900">
                    {detail.mv_id ?? "—"}
                    {detail.mv_id != null ? (
                      <MegaventoryProductExternalLink productMvId={detail.mv_id} sku={detail.sku} />
                    ) : null}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="font-medium text-slate-500">Megaventory category</dt>
                  <dd className="text-slate-900">
                    {detail.mv_category_name ?? "—"}
                    {detail.mv_category_id != null ? ` (${detail.mv_category_id})` : ""}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-slate-500">Supabase item ingest applied</dt>
                  <dd>{formatDt(detail.sb_updated)}</dd>
                </div>
                <div>
                  <dt className="font-medium text-slate-500">ERP updated</dt>
                  <dd>{formatDt(detail.erp_updated)}</dd>
                </div>
              </dl>

              <section className="space-y-3 border-b border-slate-100 pb-4">
                <h3 className="text-sm font-semibold text-slate-800">Identifiers</h3>
                <div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Label htmlFor="sku-code" className="inline">
                      SKU code
                    </Label>
                    {detail.mv_id != null ? (
                      <HoverHintWithGlyph hoverHint="Code is owned by Megaventory while this row is linked. Change it in Megaventory if needed." />
                    ) : null}
                  </div>
                  <Input
                    id="sku-code"
                    className="mt-1 font-mono"
                    disabled={detail.mv_id != null}
                    {...register("sku")}
                  />
                </div>
              </section>

              <section className="space-y-3 border-b border-slate-100 pb-4">
                <h3 className="text-sm font-semibold text-slate-800">Planning</h3>
                <div>
                  <Label htmlFor="planning-mode">Planning mode</Label>
                  <select
                    id="planning-mode"
                    {...register("planning_mode")}
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  >
                    {MRP_SKU_PLANNING_SELECT_OPTIONS.map((option) => (
                      <option key={option.value === "" ? "__not_set__" : option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Label className="inline">reserve / stock alert by location</Label>
                    <HoverHintWithGlyph
                      hoverHint="Units at each site excluded from the forecast starting bucket (subtracted from snapshot MRP on-hand for that site)."
                    />
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {MRP_LOCATIONS.map(({ code, label }) => (
                      <div key={code}>
                        <Label htmlFor={`mrp-reserve-${code}`} className="text-xs font-normal text-slate-600">
                          {label}
                        </Label>
                        <Input
                          id={`mrp-reserve-${code}`}
                          type="number"
                          className="mt-1"
                          {...register(`mrp_reserve_by_location.${code}`)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Label className="inline">Replenish to by location</Label>
                    <HoverHintWithGlyph
                      hoverHint="Target level for suggested order quantity on the safety stock alerts page (order quantity = max (0, replenish to − NET)). Must be at least the reserve for that site. Empty clears the override (database may still show twice the reserve from defaults). Reserve changes push the stock alert level to Megaventory when this SKU is linked."
                    />
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {MRP_LOCATIONS.map(({ code, label }) => (
                      <div key={`replenish-${code}`}>
                        <Label htmlFor={`mrp-replenish-${code}`} className="text-xs font-normal text-slate-600">
                          {label}
                        </Label>
                        <Input
                          id={`mrp-replenish-${code}`}
                          type="number"
                          className="mt-1"
                          {...register(`mrp_replenish_to_by_location.${code}`)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                {amlRowsQuery.isLoading ? (
                  <p className="text-sm text-slate-500">Loading AML lines for pack sizes…</p>
                ) : amlRowsQuery.error ? (
                  <p className="text-sm text-rose-700">{amlRowsQuery.error.message}</p>
                ) : (
                  <SkuStandardPackSizesSection
                    sku={props.sku}
                    amlRows={amlRows}
                    amlPackDrafts={amlPackDrafts}
                    onAmlPackDraftChange={setAmlPackDraft}
                    disabled={!hubWritesAllowed}
                    hubWritesAllowed={hubWritesAllowed}
                  />
                )}
              </section>

              <section className="space-y-3 border-b border-slate-100 pb-4">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold text-slate-800">SGS / ERP fields</h3>
                  <HoverHintWithGlyph
                    hoverHint="These columns are stored only in Supabase. They are not pushed to Megaventory."
                  />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Label className="inline">Product name</Label>
                    <HoverHintWithGlyph hoverHint="From BoM description rules. The stored sku product name column is deprecated and is not the box label title." />
                  </div>
                  <p
                    className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900"
                    aria-live="polite"
                  >
                    {detail.generated_product_name != null && detail.generated_product_name.trim() !== ""
                      ? detail.generated_product_name
                      : "—"}
                  </p>
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Label className="inline">Bar code model</Label>
                    <HoverHintWithGlyph
                      hoverHintAriaLabel="Bar code model: not editable here; assign next picks the lowest unused three-digit code on public.sku."
                      hoverHintContent={
                        <>
                          Not editable here. The button sets the lowest unused three-digit code (000 through 999) that
                          is not already used on any row in{" "}
                          <code className="rounded bg-slate-100 px-1 font-mono text-[0.85em]">public.sku</code>.
                        </>
                      }
                    />
                  </div>
                  <p
                    className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm text-slate-900"
                    aria-live="polite"
                  >
                    {detail.bar_code_model != null && detail.bar_code_model.trim() !== ""
                      ? detail.bar_code_model
                      : "—"}
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    className="mt-2"
                    isLoading={assignNextBarCodeModelMutation.isPending}
                    onClick={() => {
                      assignNextBarCodeModelMutation.mutate();
                    }}
                  >
                    Assign next bar code model
                  </Button>
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Label className="inline">ERP ID</Label>
                    <HoverHintWithGlyph hoverHint="Set by integration; not editable here." />
                  </div>
                  <p
                    className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm text-slate-900"
                    aria-live="polite"
                  >
                    {detail.erp_id != null && detail.erp_id.trim() !== "" ? detail.erp_id : "—"}
                  </p>
                </div>
                <div>
                  <Label htmlFor="template-id">Bill of materials template</Label>
                  {bomTemplatesQuery.isLoading ? (
                    <p className="mt-2 text-sm text-slate-500">Loading templates…</p>
                  ) : bomTemplatesQuery.error ? (
                    <p className="mt-2 text-sm text-rose-700">{bomTemplatesQuery.error.message}</p>
                  ) : (
                    <select
                      id="template-id"
                      {...register("template_id")}
                      className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    >
                      <option value="">No template</option>
                      {bomTemplateSelectOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Label className="inline">Megaventory bill of materials revision</Label>
                    <HoverHintWithGlyph hoverHint="Value comes from integration or ingest; it cannot be edited here. You can clear it to null." />
                  </div>
                  <p
                    className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm text-slate-900"
                    aria-live="polite"
                  >
                    {detail.mv_bom_revision != null && String(detail.mv_bom_revision) !== ""
                      ? String(detail.mv_bom_revision)
                      : "—"}
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    className="mt-2"
                    disabled={
                      detail.mv_bom_revision == null || String(detail.mv_bom_revision) === ""
                    }
                    isLoading={clearMvBomRevisionMutation.isPending}
                    onClick={() => {
                      clearMvBomRevisionMutation.mutate();
                    }}
                  >
                    Clear revision (set to null)
                  </Button>
                </div>
                {triSelect("for_sale", "For sale")}
                <label className="flex items-center gap-2 text-sm text-slate-800">
                  <input type="checkbox" {...register("serialize")} className="rounded border-slate-300" />
                  Serialize
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-800">
                  <input type="checkbox" {...register("manufacture")} className="rounded border-slate-300" />
                  Manufacture
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-800">
                  <input type="checkbox" {...register("service")} className="rounded border-slate-300" />
                  Service
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-800">
                  <input type="checkbox" {...register("build_to_order")} className="rounded border-slate-300" />
                  Build to order
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-800">
                  <input type="checkbox" {...register("for_sale_deployed")} className="rounded border-slate-300" />
                  For sale deployed
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-800">
                  <input type="checkbox" {...register("advertised")} className="rounded border-slate-300" />
                  Advertised
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-800">
                  <input type="checkbox" {...register("obsolete")} className="rounded border-slate-300" />
                  Obsolete
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    {...register("under_engineering_review")}
                    className="rounded border-slate-300"
                  />
                  Under engineering review
                </label>
                {triSelect(
                  "cots",
                  "Commercial off the shelf (COTS)",
                  detail.mv_category_name != null &&
                    ["product", "accessory", "service", "tool", "label"].includes(
                      detail.mv_category_name.trim().toLowerCase(),
                    ),
                )}
                <label className="flex items-center gap-2 text-sm text-slate-800">
                  <input type="checkbox" {...register("erp_skip_import")} className="rounded border-slate-300" />
                  ERP skip import
                </label>
              </section>

              <section className="space-y-3 pb-4">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold text-slate-800">Hub supply chain notes</h3>
                  <HoverHintWithGlyph
                    hoverHint="Extended notes stored only in Supabase. Megaventory ProductComments are limited to 400 characters; use the mirrored field below for what appears in Megaventory."
                  />
                </div>
                <div>
                  <Label htmlFor="hub-notes">Hub supply chain notes</Label>
                  <TextArea id="hub-notes" className="mt-1" rows={6} {...register("hub_supply_chain_notes")} />
                </div>
              </section>

              <section className="space-y-3 pb-4">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold text-slate-800">Commercial invoice</h3>
                  <HoverHintWithGlyph hoverHint="International commercial invoices cannot declare $0. When a sales order line is $0, the invoice uses this fair market value instead." />
                </div>
                <div>
                  <Label htmlFor="fair-market-value">Fair market value (USD)</Label>
                  <Input id="fair-market-value" className="mt-1" inputMode="decimal" {...register("fair_market_value")} />
                </div>
              </section>

              <section className="space-y-3 pb-4">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold text-slate-800">Megaventory-mirrored fields</h3>
                  {detail.mv_id != null ? (
                    <HoverHintWithGlyph
                      hoverHintAriaLabel="Saving changes calls Megaventory ProductUpdate (merge by default; full product row with Update when clearing mirrored text), then updates public.sku."
                      hoverHintContent={
                        <>
                          Saving changes calls Megaventory{" "}
                          <code className="rounded bg-slate-100 px-1 font-mono text-[0.85em]">ProductUpdate</code>{" "}
                          with <code className="rounded bg-slate-100 px-1 font-mono text-[0.85em]">InsertOrUpdateNonEmptyFields</code>{" "}
                          (only non-empty fields you change are sent; clearing a mirrored text field uses a full Megaventory product row with <code className="rounded bg-slate-100 px-1 font-mono text-[0.85em]">Update</code>), then updates{" "}
                          <code className="rounded bg-slate-100 px-1 font-mono text-[0.85em]">public.sku</code>.
                        </>
                      }
                    />
                  ) : (
                    <HoverHintWithGlyph hoverHint="Description and variant are stored in the hub until this SKU is linked to Megaventory. Save, then use Add to Megaventory to create the product with these values." />
                  )}
                </div>
                {catalogDescriptionControlled ? (
                  <p className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
                    Description and variant are controlled by the BoM template from{" "}
                    <code className="rounded bg-white px-1 font-mono text-[0.85em]">generate_description_parts</code>.
                    Edit description rules in{" "}
                    <Link
                      to={buildSetBomRulesDeepLink(props.sku, { tab: "parse" })}
                      className="font-medium text-brand-700 underline hover:text-brand-900"
                    >
                      Set BoM rules
                    </Link>
                    . Saved catalog text syncs to Megaventory when it differs.
                  </p>
                ) : null}
                <div>
                  <Label htmlFor="mv-desc">Description</Label>
                  {catalogDescriptionControlled ? (
                    <div className="mt-1 space-y-1">
                      <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                        {detail.generated_mv_description?.trim() || detail.mv_description?.trim() || "—"}
                      </p>
                      {detail.generated_mv_description != null &&
                      detail.mv_description != null &&
                      detail.generated_mv_description.trim() !== detail.mv_description.trim() ? (
                        <p className="text-xs text-amber-800">
                          Saved catalog text differs; sync runs automatically when BoM rules change.
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <TextArea id="mv-desc" className="mt-1" {...register("mv_description")} />
                  )}
                </div>
                <div>
                  <Label htmlFor="mv-variant">Variant</Label>
                  {catalogDescriptionControlled ? (
                    <p className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                      {detail.generated_mv_variant?.trim() || detail.mv_variant?.trim() || "—"}
                    </p>
                  ) : (
                    <Input id="mv-variant" className="mt-1" {...register("mv_variant")} />
                  )}
                </div>
                <div>
                  <Label htmlFor="mv-cat">Category (Megaventory)</Label>
                  {productCategoriesQuery.isLoading ? (
                    <p className="mt-2 text-sm text-slate-500">Loading categories…</p>
                  ) : productCategoriesQuery.error ? (
                    <p className="mt-2 text-sm text-rose-700">{productCategoriesQuery.error.message}</p>
                  ) : (
                    <select
                      id="mv-cat"
                      {...register("mv_category_id")}
                      className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                    >
                      {(watchedCategoryId ?? "") === "" ? (
                        <option value="">Select a category</option>
                      ) : null}
                      {categorySelectOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {detail.mv_id == null ? (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-500">
                      Not linked to Megaventory yet. Set description (required), variant, and category above, save, then
                      create the Megaventory product from this hub row.
                    </p>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={!hubWritesAllowed || hasUnsavedChanges}
                      isLoading={createMegaventoryProductMutation.isPending}
                      onClick={() => {
                        createMegaventoryProductMutation.mutate();
                      }}
                    >
                      Add to Megaventory
                    </Button>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Label htmlFor="mv-type" className="inline">
                          Product type
                        </Label>
                        <HoverHintWithGlyph
                          hoverHintAriaLabel="Megaventory accepts only enumerated ProductType values, not free text. Link opens ProductUpdate metadata."
                          hoverHintContent={
                            <>
                              Megaventory accepts only enumerated{" "}
                              <a
                                href="https://api.megaventory.com/v2017a/json/metadata?op=ProductUpdate"
                                target="_blank"
                                rel="noreferrer"
                                className="pointer-events-auto text-brand-600 underline"
                              >
                                ProductType
                              </a>{" "}
                              values (not free text).
                            </>
                          }
                        />
                      </div>
                      {productTypesQuery.isLoading ? (
                        <p className="mt-2 text-sm text-slate-500">Loading product types…</p>
                      ) : productTypesQuery.error ? (
                        <p className="mt-2 text-sm text-rose-700">{productTypesQuery.error.message}</p>
                      ) : (
                        <select
                          id="mv-type"
                          {...register("mv_product_type")}
                          className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 font-mono text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        >
                          {(watchedProductType ?? "").trim() === "" ? (
                            <option value="">Select product type</option>
                          ) : null}
                          {productTypeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    {triSelect("mv_sellable", "Sellable")}
                    {triSelect("mv_purchasable", "Purchasable")}
                    <div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Label htmlFor="mv-unit" className="inline">
                          Unit of measure
                        </Label>
                        <HoverHintWithGlyph
                          hoverHintAriaLabel="Megaventory ProductUnitOfMeasurement from the hub unit catalog. Link opens ProductUpdate metadata."
                          hoverHintContent={
                            <>
                              Megaventory{" "}
                              <a
                                href="https://api.megaventory.com/v2017a/json/metadata?op=ProductUpdate"
                                target="_blank"
                                rel="noreferrer"
                                className="pointer-events-auto text-brand-600 underline"
                              >
                                ProductUnitOfMeasurement
                              </a>{" "}
                              must match a hub catalog unit (or one already used on a product). MRP write access can
                              create new catalog units below.
                            </>
                          }
                        />
                      </div>
                      {measurementUnitsQuery.isLoading ? (
                        <p className="mt-2 text-sm text-slate-500">Loading units of measure…</p>
                      ) : measurementUnitsQuery.error ? (
                        <p className="mt-2 text-sm text-rose-700">{measurementUnitsQuery.error.message}</p>
                      ) : (
                        <>
                          <select
                            id="mv-unit"
                            {...register("mv_unit")}
                            disabled={!hubWritesAllowed}
                            className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500"
                          >
                            {(watchedMvUnit ?? "").trim() === "" ? (
                              <option value="">Select unit of measure</option>
                            ) : null}
                            {measurementUnitOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          {hubWritesAllowed ? (
                            <div className="mt-2 flex flex-wrap items-end gap-2">
                              <div className="min-w-[10rem] flex-1">
                                <Label htmlFor="mv-unit-create">Create unit of measure</Label>
                                <Input
                                  id="mv-unit-create"
                                  className="mt-1"
                                  value={newUnitName}
                                  onChange={(event) => setNewUnitName(event.target.value)}
                                  placeholder="e.g. Roll"
                                  maxLength={64}
                                  disabled={createUnitMutation.isPending}
                                />
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="secondary"
                                disabled={
                                  createUnitMutation.isPending || newUnitName.trim() === ""
                                }
                                isLoading={createUnitMutation.isPending}
                                onClick={() => createUnitMutation.mutate(newUnitName.trim())}
                              >
                                Create
                              </Button>
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="mv-image">Image URL</Label>
                      <Input id="mv-image" className="mt-1" {...register("mv_image_url")} />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Label htmlFor="mv-unit-cost">Landed unit cost (USD)</Label>
                        <HoverHintWithGlyph hoverHint="Megaventory ProductUnitCost for company currency. Updated on item and inventory sync. Not editable here; ProductPurchasePrice and ProductSellingPrice are ignored form prefills." />
                      </div>
                      <Input
                        id="mv-unit-cost"
                        className="mt-1"
                        readOnly
                        value={
                          detail.mv_unit_cost == null || detail.mv_unit_cost === ""
                            ? "—"
                            : String(detail.mv_unit_cost)
                        }
                      />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Label htmlFor="mv-notes">Megaventory comments (≤400 characters)</Label>
                        <HoverHintWithGlyph hoverHint="Mirrors Megaventory ProductComments. Saving pushes this text to Megaventory; longer notes belong in hub supply chain notes above." />
                      </div>
                      <TextArea id="mv-notes" className="mt-1" rows={4} maxLength={400} {...register("mv_supply_chain_notes")} />
                    </div>
                    <div>
                      <Label htmlFor="mv-hts">HTS code (MV custom field 2)</Label>
                      <Input id="mv-hts" className="mt-1 font-mono" {...register("mv_hts_code")} />
                    </div>
                  </>
                )}
              </section>
            </fieldset>
            </form>
          ) : null}
      </div>

      <div
        className="pointer-events-none fixed inset-x-3 bottom-3 z-40 flex justify-end sm:inset-x-auto sm:right-6 sm:bottom-6"
        aria-hidden={false}
      >
        <div
          className="pointer-events-auto rounded-lg border border-slate-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur-sm"
          role="toolbar"
          aria-label="Save SKU properties"
        >
          <HubSaveButton
            type="submit"
            form="sku-edit-form"
            dirty={hasUnsavedChanges}
            dirtyHint={dirtyFieldHint}
            disabled={
              !hubWritesAllowed ||
              !detail ||
              detailQuery.isLoading ||
              clearMvBomRevisionMutation.isPending ||
              assignNextBarCodeModelMutation.isPending ||
              createMegaventoryProductMutation.isPending
            }
            blockedHint={mrpReadOnlyTitle}
            isLoading={saveMutation.isPending}
          >
            Save changes
          </HubSaveButton>
        </div>
      </div>
    </div>
  );
}
